import { useEffect, useMemo, useState } from 'react';
import { useVimOsContext } from './useVimOsContext';
import type { EHR } from 'vim-os-js-browser/types';

export const useUpdateEncounter = (
  paramsToCheck?: EHR.CanUpdateEncounterParams
) => {
  const vimOS = useVimOsContext();

  const [canUpdateParams, setCanUpdateParams] = useState<boolean>(false);

  useEffect(() => {
    // Don't do anything if SDK is not available
    if (!vimOS?.ehr?.resourceUpdater) {
      return;
    }

    // Set initial value
    if (paramsToCheck) {
      setCanUpdateParams(
        vimOS.ehr.resourceUpdater.canUpdateEncounter(paramsToCheck).canUpdate
      );
    }

    const cb = () =>
      setCanUpdateParams(
        paramsToCheck
          ? vimOS.ehr.resourceUpdater.canUpdateEncounter(paramsToCheck)
              .canUpdate
          : false
      );

    vimOS.ehr.resourceUpdater.subscribe('encounter', cb);
    return () => vimOS.ehr.resourceUpdater.unsubscribe('encounter', cb);
  }, [vimOS?.ehr?.resourceUpdater, paramsToCheck]);

  return useMemo(
    () => ({
      canUpdateParams,
      // Return with demo's expected names
      canUpdate: (params: EHR.CanUpdateEncounterParams) => {
        if (!vimOS?.ehr?.resourceUpdater) {
          return { canUpdate: false, details: { error: 'SDK not available' } };
        }
        return vimOS.ehr.resourceUpdater.canUpdateEncounter(params);
      },
      updateVimEncounter: async (params: EHR.UpdateEncounterParams) => {
        if (!vimOS?.ehr?.resourceUpdater) {
          throw new Error('Vim SDK not available');
        }
        return vimOS.ehr.resourceUpdater.updateEncounter(params);
      },
      isVimSDKAvailable: !!vimOS?.ehr?.resourceUpdater,
    }),
    [vimOS?.ehr?.resourceUpdater, canUpdateParams]
  );
};
